import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Upload, File, X } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useFileUpload } from '../hooks/useSalesDocs';
import { deriveFileName } from '../constants';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload } from '../types';

interface SalesDocFormModalProps {
  open: boolean;
  doc?: SalesDoc | null;
  loading?: boolean;
  onClose: () => void;
  onCreate: (payload: CreateSalesDocPayload) => void;
  onUpdate: (id: string, payload: UpdateSalesDocPayload) => void;
}

export function SalesDocFormModal({ open, doc, loading, onClose, onCreate, onUpdate }: SalesDocFormModalProps) {
  const isEdit = !!doc;

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useFileUpload();

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevDoc, setPrevDoc] = useState(doc);

  if (open !== prevOpen || doc !== prevDoc) {
    setPrevOpen(open);
    setPrevDoc(doc);
    if (open) {
      setTitle(doc?.title ?? '');
      setUrl(doc?.url ?? '');
    }
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (returnedUrl: string) => {
        setUrl(returnedUrl);
        if (!title.trim()) setTitle(file.name);
        toast.success('File uploaded.');
      },
      onError: () => toast.error('Upload failed.'),
    });
  };

  const removeFile = () => {
    setUrl('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required.'); return; }
    if (!url.trim()) { toast.error('Add a document URL or upload a file.'); return; }

    const payload: CreateSalesDocPayload = {
      title: title.trim(),
      url: url.trim(),
    };

    if (isEdit) { onUpdate(doc!.id, payload); } else { onCreate(payload); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Document' : 'New Document'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" form="salesdoc-form" loading={loading}>{isEdit ? 'Save Changes' : 'Add Document'}</Button>
        </>
      }
    >
      <form id="salesdoc-form" onSubmit={submit} className="space-y-4">
        <Input label="Title *" name="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title…" required />

        <div className="space-y-2">
          <label className="mb-1.5 block text-body-sm font-medium text-on-surface">Document File</label>
          <div className="flex flex-col gap-2">
            {url ? (
              <div className="flex items-center justify-between p-3 rounded-DEFAULT border border-outline bg-surface">
                <div className="flex items-center gap-3 min-w-0">
                  <File className="h-5 w-5 text-on-surface-variant flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="block truncate text-sm font-medium">{deriveFileName(url)}</span>
                    <span className="text-xs text-on-surface-variant">Uploaded file</span>
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={removeFile} className="text-destructive hover:bg-destructive/10">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
                  <Upload className="h-4 w-4" /> Upload file
                </Button>
                <Input
                  type="text"
                  placeholder="https://… or upload a file"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1"
                />
              </div>
            )}
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default SalesDocFormModal;
